"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "@/i18n/routing";
import { useTranslations } from "next-intl";
import { Loader2, Search, X } from "lucide-react";
import { track } from "@/lib/tracking/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type FilterOption = { value: string; label: string };

type FilterElement = "search" | "filter" | "button";

interface FilterBarProps {
  locale: string;
  /** Options for the dropdown (already localized). */
  options: FilterOption[];
  /** URL query param name used for the dropdown filter (e.g. "categoryId", "newsTypeId"). */
  filterParam: string;
  /** Element id of the results container to scroll to after submitting. */
  scrollTargetId: string;
  /** Placeholder / label shown on the trigger when no option is selected. */
  filterPlaceholder: string;
  /** Label for the "all" option in the dropdown. Defaults to common "all". */
  allOptionLabel?: string;
  searchPlaceholder: string;
  /** Initial values from server; kept for SSR/hydration. Client syncs from URL. */
  defaultSearch?: string;
  defaultFilterId?: number;
  showSearch?: boolean;
  /** Order of the three elements in the form. Defaults to search, filter, button. */
  order?: FilterElement[];
  /** When true, reverse the row direction under RTL locales. */
  reverseOnRtl?: boolean;
  formId?: string;
}

export function FilterBar({
  locale,
  options,
  filterParam,
  scrollTargetId,
  filterPlaceholder,
  allOptionLabel,
  searchPlaceholder,
  defaultSearch = "",
  defaultFilterId = 0,
  showSearch = true,
  order = ["search", "filter", "button"],
  reverseOnRtl = false,
  formId,
}: FilterBarProps) {
  const tCommon = useTranslations("common");
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const isRtl = locale === "ar";
  const allLabel = allOptionLabel ?? tCommon("all");

  const searchFromUrl = searchParams.get("search") ?? "";
  const filterFromUrl = searchParams.get(filterParam) ?? "";
  const pageSizeFromUrl = searchParams.get("pageSize") ?? "";

  const [searchInput, setSearchInput] = useState(
    () => searchFromUrl || defaultSearch
  );
  const [filterValue, setFilterValue] = useState<string>(() => {
    const fromUrl = filterFromUrl && filterFromUrl !== "0" ? filterFromUrl : "";
    const fromProp = defaultFilterId > 0 ? String(defaultFilterId) : "";
    return fromUrl || fromProp || "all";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const justSearchedRef = useRef(false);

  // Sync local state when URL changes (e.g. back/forward or external navigation)
  useEffect(() => {
    setSearchInput(searchParams.get("search") ?? "");
    const val = searchParams.get(filterParam) ?? "";
    setFilterValue(val && val !== "0" ? val : "all");
  }, [searchParams, filterParam]);

  // Clear loading and scroll to results when navigation completes after search
  useEffect(() => {
    setIsSubmitting(false);
    if (justSearchedRef.current) {
      justSearchedRef.current = false;
      requestAnimationFrame(() => {
        document
          .getElementById(scrollTargetId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [searchParams, scrollTargetId]);

  // Fallback: clear loading after timeout
  useEffect(() => {
    if (!isSubmitting) return;
    const fallback = setTimeout(() => setIsSubmitting(false), 3000);
    return () => clearTimeout(fallback);
  }, [isSubmitting]);

  const selectedLabel = useMemo(() => {
    if (filterValue === "all") return filterPlaceholder;
    return options.find((o) => o.value === filterValue)?.label ?? filterPlaceholder;
  }, [filterValue, options, filterPlaceholder]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    justSearchedRef.current = true;
    const params = new URLSearchParams(searchParams.toString());
    const searchTrimmed = searchInput.trim();
    if (searchTrimmed) {
      params.set("search", searchTrimmed);
      try {
        track("Search", { search_string: searchTrimmed });
      } catch {
        // ignore
      }
    } else {
      params.delete("search");
    }
    if (filterValue && filterValue !== "all") {
      params.set(filterParam, filterValue);
    } else {
      params.delete(filterParam);
    }
    params.delete("page"); // Reset to first page when filtering
    if (pageSizeFromUrl) params.set("pageSize", pageSizeFromUrl);
    const query = params.toString();
    router.push(query ? `${pathname}?${query}` : pathname);
  };

  const searchBlock = showSearch ? (
    <div key="search" className="w-full md:flex-[1.5] relative">
      <span className="absolute right-4 rtl:right-auto rtl:left-4 inset-y-0 flex items-center text-gray-500 pointer-events-none">
        <Search className="h-5 w-5" />
      </span>
      {searchInput ? (
        <button
          type="button"
          onClick={() => setSearchInput("")}
          aria-label={tCommon("clear")}
          className="absolute right-12 rtl:right-auto rtl:left-12 inset-y-0 flex items-center gap-1 text-gray-400 hover:text-gray-600"
        >
          <span className="text-sm">{tCommon("clear")}</span>
          <X className="h-4 w-4" />
        </button>
      ) : null}
      <input
        type="text"
        name="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="w-full h-12 md:h-14 px-4 pr-12 rtl:pr-4 rtl:pl-12 border border-gray-200 rounded-xl text-right"
        placeholder={searchPlaceholder}
      />
    </div>
  ) : null;

  const filterBlock = (
    <div key="filter" className="w-full md:flex-1">
      <Select
        dir={isRtl ? "rtl" : "ltr"}
        value={filterValue}
        onValueChange={setFilterValue}
      >
        <SelectTrigger
          style={{ height: "50px" }}
          className="w-full px-4 border border-gray-200 rounded-xl bg-white text-right"
        >
          <SelectValue placeholder={filterPlaceholder}>{selectedLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent
          position="popper"
          align="center"
          className="max-h-64 w-[var(--radix-select-trigger-width)]"
        >
          <SelectItem value="all">{allLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const buttonBlock = (
    <div key="button" className="w-full md:w-[220px]">
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-12 md:h-14 px-6 bg-primary text-primary-foreground rounded-xl font-bold hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            {tCommon("processing")}
          </>
        ) : (
          tCommon("search")
        )}
      </button>
    </div>
  );

  const blocks: Record<FilterElement, React.ReactNode> = {
    search: searchBlock,
    filter: filterBlock,
    button: buttonBlock,
  };

  const rowDir = reverseOnRtl && isRtl ? "md:flex-row-reverse" : "md:flex-row";

  return (
    <div className="filterationArea mb-8">
      <form
        className={`bg-white rounded-2xl p-4 md:p-6 flex flex-col md:items-center gap-4 ${rowDir}`}
        onSubmit={handleSubmit}
        id={formId}
      >
        {order.map((el) => blocks[el])}
      </form>
    </div>
  );
}
